import React, { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI, userAPI, userV2API } from '../../services/api';
import { tokens } from '../../theme';
import { AnalyticsHero, AnalyticsScreen, analyticsTokens, editorialSerif, panelSx } from './analytics/shared';
import cardMountains from '../../assets/card-mountains.png';
import { buildLoginUrl } from '../../utils/authRedirect';

export default function UserDashboardSettings({ onNavigateSupport, onOpenProductSubmission, onSignOut }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, previewMode } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profile, setProfile] = useState({ name: '', age: '', weight: '', height: '', gender: 'male', timezone: 'UTC' });
  const [preferences, setPreferences] = useState({
    notificationsEnabled: true,
    darkThemeEnabled: true,
    useMetricSystem: true,
    fastingRemindersEnabled: true,
    showMotivationalQuotes: true,
  });
  const [nutritionPrefs, setNutritionPrefs] = useState({
    calorieStrategy: '',
    proteinTargetG: '',
    fiberTargetG: '',
    sodiumAwareness: '',
    dietStyle: '',
    preferredFastBreakingMealType: '',
  });
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });

  useEffect(() => {
    // Reachable at /dashboard/settings via the nav rail. The preview user has
    // no tokens, so these requests would 401 and the auth interceptor would
    // bounce the preview session to /login instead of showing the settings
    // form. previewMode comes from AuthContext (captured once at session
    // establishment), not re-derived from window.location.search, which
    // in-app navigation rewrites without the query string.
    if (!user || previewMode) {
      setLoading(false);
      return;
    }
    const loadData = async () => {
      setLoading(true);
      try {
        const response = await userAPI.get(user.id);
        const data = response.data?.data || {};
        setProfile({
          name: data.name || '',
          age: data.age ?? '',
          weight: data.weight ?? '',
          height: data.height ?? '',
          gender: data.gender || 'male',
          timezone: data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        });
        const prefs = data.preferences || {};
        setPreferences({
          notificationsEnabled: prefs.notificationsEnabled !== false,
          darkThemeEnabled: prefs.darkThemeEnabled !== false,
          useMetricSystem: prefs.useMetricSystem !== false,
          fastingRemindersEnabled: prefs.fastingRemindersEnabled !== false,
          showMotivationalQuotes: prefs.showMotivationalQuotes !== false,
        });
        const nutritionRes = await userV2API.getNutritionPreferences(user.id).catch(() => null);
        const nutrition = nutritionRes?.data?.data || {};
        setNutritionPrefs({
          calorieStrategy: nutrition.calorieStrategy || '',
          proteinTargetG: nutrition.proteinTargetG ?? '',
          fiberTargetG: nutrition.fiberTargetG ?? '',
          sodiumAwareness: nutrition.sodiumAwareness || '',
          dietStyle: nutrition.dietStyle || '',
          preferredFastBreakingMealType: nutrition.preferredFastBreakingMealType || '',
        });
      } catch (err) {
        setError(err.message || 'Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [previewMode, user]);

  const handleSaveProfile = async () => {
    if (!user) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await userV2API.patchProfile(user.id, {
        displayName: profile.name,
        age: Number(profile.age),
        weight: Number(profile.weight),
        height: Number(profile.height),
        biologicalSex: profile.gender,
        timezone: profile.timezone,
      });
      setSuccess('Profile updated.');
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNutritionPreferences = async () => {
    if (!user) return;
    setSaving(true);
    try {
      await userV2API.patchNutritionPreferences(user.id, {
        calorieStrategy: nutritionPrefs.calorieStrategy || '',
        proteinTargetG: nutritionPrefs.proteinTargetG === '' ? null : Number(nutritionPrefs.proteinTargetG),
        fiberTargetG: nutritionPrefs.fiberTargetG === '' ? null : Number(nutritionPrefs.fiberTargetG),
        sodiumAwareness: nutritionPrefs.sodiumAwareness || '',
        dietStyle: nutritionPrefs.dietStyle || '',
        preferredFastBreakingMealType: nutritionPrefs.preferredFastBreakingMealType || '',
      });
      setSuccess('Nutrition preferences updated.');
    } catch (err) {
      setError(err.message || 'Failed to save nutrition preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePreferences = async () => {
    if (!user) return;
    setSaving(true);
    try {
      await userAPI.updatePreferences(user.id, preferences);
      setSuccess('Preferences updated.');
    } catch (err) {
      setError(err.message || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordForm;
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All password fields are required.');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    setSaving(true);
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      setSuccess('Password changed. Sign in again and we will bring you right back into FastTime.');
      setTimeout(async () => {
        await logout();
        navigate(buildLoginUrl({ reason: 'password-updated' }), { replace: true });
      }, 1800);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Typography sx={{ color: tokens.textMuted, py: 4, textAlign: 'center' }}>Loading settings…</Typography>;
  }

  return (
    <AnalyticsScreen>
      <AnalyticsHero title="Settings" subtitle="Profile, preferences, security, and account actions." backgroundImage={cardMountains} />
      {(error || success) && <Alert severity={error ? 'error' : 'success'} sx={{ mb: 2 }} onClose={() => { setError(''); setSuccess(''); }}>{error || success}</Alert>}
      <Grid container spacing={1.5}>
        <Grid item xs={12} lg={7}>
          <Card sx={panelSx()}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: 'rgba(169,191,122,0.14)', color: tokens.accentStrong }}>{(profile.name?.[0] || user?.email?.[0] || 'F').toUpperCase()}</Avatar>
                <Box>
                  <Typography variant="h5" sx={{ color: tokens.cream, fontFamily: editorialSerif }}>Profile</Typography>
                  <Typography variant="body2" sx={{ color: tokens.textMuted }}>Health basics and account details</Typography>
                </Box>
              </Stack>
              <Stack spacing={1.5}>
                <TextField fullWidth label="Name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField fullWidth label="Age" type="number" value={profile.age} onChange={(e) => setProfile({ ...profile, age: e.target.value })} />
                  <TextField fullWidth label="Gender" value={profile.gender} onChange={(e) => setProfile({ ...profile, gender: e.target.value })} />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField fullWidth label="Weight" type="number" value={profile.weight} onChange={(e) => setProfile({ ...profile, weight: e.target.value })} />
                  <TextField fullWidth label="Height" type="number" value={profile.height} onChange={(e) => setProfile({ ...profile, height: e.target.value })} />
                </Stack>
                <TextField
                  fullWidth
                  label="Timezone"
                  value={profile.timezone}
                  onChange={(e) => setProfile({ ...profile, timezone: e.target.value })}
                  helperText="IANA timezone used for daily tracking boundaries"
                />
                <Button variant="contained" onClick={handleSaveProfile} disabled={saving}>Save profile</Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Card sx={panelSx()}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="h5" sx={{ color: tokens.cream, fontFamily: editorialSerif, mb: 2 }}>Preferences</Typography>
              <Stack spacing={0.5}>
                {[
                  ['Notifications', 'notificationsEnabled'],
                  ['Dark theme', 'darkThemeEnabled'],
                  ['Metric system', 'useMetricSystem'],
                  ['Fasting reminders', 'fastingRemindersEnabled'],
                  ['Motivational quotes', 'showMotivationalQuotes'],
                ].map(([label, key]) => (
                  <FormControlLabel
                    key={key}
                    control={<Switch checked={preferences[key]} onChange={(e) => setPreferences({ ...preferences, [key]: e.target.checked })} />}
                    label={<Typography sx={{ color: tokens.textSoft }}>{label}</Typography>}
                    sx={{ m: 0, justifyContent: 'space-between' }}
                  />
                ))}
              </Stack>
              <Button variant="outlined" sx={{ mt: 2 }} onClick={handleSavePreferences} disabled={saving}>Save preferences</Button>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card sx={panelSx()}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="h5" sx={{ color: tokens.cream, fontFamily: editorialSerif, mb: 2 }}>Nutrition preferences</Typography>
              <Stack spacing={1.5}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField
                    fullWidth
                    label="Calorie strategy"
                    select
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ native: true }}
                    value={nutritionPrefs.calorieStrategy}
                    onChange={(e) => setNutritionPrefs({ ...nutritionPrefs, calorieStrategy: e.target.value })}
                  >
                    <option value="">Not set</option>
                    {['deficit', 'maintenance', 'surplus', 'flexible'].map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    label="Diet style"
                    select
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ native: true }}
                    value={nutritionPrefs.dietStyle}
                    onChange={(e) => setNutritionPrefs({ ...nutritionPrefs, dietStyle: e.target.value })}
                  >
                    <option value="">Not set</option>
                    {['balanced', 'high_protein', 'low_carb', 'mediterranean', 'plant_forward', 'keto', 'other'].map((v) => (
                      <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>
                    ))}
                  </TextField>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField
                    fullWidth
                    label="Protein target (g)"
                    type="number"
                    value={nutritionPrefs.proteinTargetG}
                    onChange={(e) => setNutritionPrefs({ ...nutritionPrefs, proteinTargetG: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    label="Fiber target (g)"
                    type="number"
                    value={nutritionPrefs.fiberTargetG}
                    onChange={(e) => setNutritionPrefs({ ...nutritionPrefs, fiberTargetG: e.target.value })}
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField
                    fullWidth
                    label="Sodium awareness"
                    select
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ native: true }}
                    value={nutritionPrefs.sodiumAwareness}
                    onChange={(e) => setNutritionPrefs({ ...nutritionPrefs, sodiumAwareness: e.target.value })}
                  >
                    <option value="">Not set</option>
                    {['low', 'moderate', 'high'].map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    label="Preferred breaking-fast meal"
                    select
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ native: true }}
                    value={nutritionPrefs.preferredFastBreakingMealType}
                    onChange={(e) => setNutritionPrefs({ ...nutritionPrefs, preferredFastBreakingMealType: e.target.value })}
                  >
                    <option value="">Not set</option>
                    {['breakfast', 'lunch', 'light_snack', 'protein_focused'].map((v) => (
                      <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>
                    ))}
                  </TextField>
                </Stack>
                <Button variant="contained" onClick={handleSaveNutritionPreferences} disabled={saving}>
                  Save nutrition preferences
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={7}>
          <Card sx={panelSx()}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="h5" sx={{ color: tokens.cream, fontFamily: editorialSerif, mb: 2 }}>Password & security</Typography>
              <Stack spacing={1.5}>
                <TextField fullWidth label="Current password" type="password" value={passwordForm.currentPassword} onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })} />
                <TextField fullWidth label="New password" type="password" value={passwordForm.newPassword} onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} />
                <TextField fullWidth label="Confirm password" type="password" value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} />
                <Button variant="contained" onClick={handleChangePassword} disabled={saving}>Update password</Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card sx={panelSx()}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="h5" sx={{ color: tokens.cream, fontFamily: editorialSerif, mb: 2 }}>Account actions</Typography>
              <Stack spacing={1.5}>
                <Button variant="outlined" onClick={() => navigate({ pathname: '/billing', search: location.search })}>Open billing</Button>
                {onNavigateSupport && (
                  <Button variant="outlined" onClick={onNavigateSupport}>Contact support</Button>
                )}
                {onOpenProductSubmission && (
                  <Button variant="outlined" onClick={onOpenProductSubmission}>Submit food product</Button>
                )}
                <Divider sx={{ borderColor: analyticsTokens.border }} />
                {onSignOut && (
                  <Button variant="outlined" onClick={onSignOut}>
                    Sign out
                  </Button>
                )}
                <Button variant="outlined" onClick={() => navigate({ pathname: '/delete-account', search: location.search })} sx={{ color: tokens.danger, borderColor: 'rgba(230,124,115,0.28)' }}>Delete account</Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </AnalyticsScreen>
  );
}
